import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { CATEGORY_SLUGS } from "@shared/constants/categories";
import { SECTION_SLUGS } from "@shared/constants/sections";
import { FIELD_KEYS } from "@shared/constants/fields";
import { screenByKey } from "@shared/interview/chapters";
import { boardCell, boardOptions, boardStep, boardTable, boardTiles } from "@shared/interview/board";
import { useSectionRows, type SectionRow } from "./useSectionRows";
import { saveInBackground, useSaveFailure } from "./saveFailure";
import { TilePicker, type TileSelection } from "./TilePicker";
import { TileTables, type TiledRow } from "./TileTables";
import { ContactCard, LinkedPersonCell, cardSummary, type CardSlot } from "./PersonPicker";
import { ConsequencePicker } from "./ConsequencePicker";
import { IconTextCell, NoteCard } from "./InterviewCells";
import { OnePanelProvider, usePanels } from "./useOnePanelOpen";
import { parseContact } from "@shared/contact";

/**
 * s224 — PETS OR OTHER ANIMALS (SPEC: `Main.dc.html`, the `pets` step; D-223.36, RULE 137).
 *
 * 🔴 THE TYPED-IN VERSION IS GONE. This file used to be the s205 shell pilot, built before any screen
 * could read the board: eleven species TILES, its own copy of the title and the why line, a hand-typed
 * "None of these", two loose text boxes, and no table at all. D-223.34 measured it against board 1 and
 * ALL EIGHT compared properties differed — and the board's one real question, *"Who would take them?"*,
 * was absent entirely from a screen whose why line is *"An animal needs someone the same day."*
 *
 * It was not carelessness and the finding is worth keeping: every chapter-one screen that read the board
 * at run time matched it, and both that did not had drifted. So the fix is not to re-type the board's
 * words correctly — that is how this screen began, and the stand-in is what drifts. Everything below
 * comes from the board through `boardWords` / `boardTiles` / `boardTable` / `boardCell` / `boardOptions`.
 * Nothing the board declares is typed here. (RULE 137, and `PetsScreen.test.tsx` enforces it by
 * comparing the SCREEN against the BOARD STEP, so re-typing a literal would not satisfy it either.)
 *
 * ## Two tiles, two tables — the vet is its own ROW (D-223.36 ruling 1, Reade: "agree")
 * The board declares `pet` and `vet`, and `second: { tiles: ['vet'] }`. Both are rows of `family-pets`,
 * told apart by `row_kind` and split for drawing by the shared `TileTables` — the `kids` precedent
 * exactly (D-221.10: one section, a kind column; the split is how rows are DRAWN, not how they are
 * stored). Storage used to keep `vet` / `vet_phone` / `vet_email` / `vet_address` as columns on EVERY
 * pet row, so three pets meant the vet written three times — four copies of one organisation, which is
 * what D-202.1 ("one contact record, referenced everywhere") exists to stop. Those four are RETIRED.
 *
 * ## The species list is the board's (D-223.36 ruling 3)
 * `pet_type` stored twelve; the board offers seven, and `OPTION_VALUE.pets` in `board-map.ts` links the
 * board's words to the stored values. Species are NOT tiles — the old `PET_TILES` array is deleted.
 *
 * ## Writes — the two shipped paths, no third (interview-write-paths.test.ts)
 *  • facts → `POST /api/clients/:id/generic-entries`, per cell, through `useSectionRows` (D-210.18);
 *  • flags → `PATCH /api/clients/:id/situation-flags { hasPets }` — ANSWER-FLAG-WRITE-1, kept exactly.
 *    `secure-pets` is the one task gated on `has_pets`, so the None tile's `false` prunes one job. The
 *    counter itself is the board's `none.prune`, read by the shell through `TilePicker` — this screen
 *    adds no display of a declared task count (INTERVIEW-COUNTER-TRUTH-1).
 *
 * ## 🔴 Deselect HIDES, never deletes (G8)
 * Turning a tile off, "No animals", and × all set `isHidden: true` and send NO value, so nothing a row
 * held is changed and turning the tile back on restores it. There is no DELETE in this file.
 *
 * ## Left exactly as they are
 * `breed`, `insurance` and `notes` are not on the board and nobody has ruled on them, so the planner
 * section keeps them untouched (D-223.36's "not ruled, so not touched").
 */

const PETS = boardStep("pets");
const PETS_TILES = boardTiles(PETS);
const TILES = PETS_TILES.tiles.map(({ id, label, sub, mark, color }) => ({ id, label, sub, mark, color }));
const NOTE_FOR = PETS.noteFor ?? {};

const CELL = {
  kind: boardCell(PETS, "kind"),
  name: boardCell(PETS, "name"),
  who: boardCell(PETS, "who"),
  call: boardCell(PETS, "call"),
};
export const PET_KINDS = boardOptions("pets", CELL.kind);

export const PET_TABLE = boardTable(PETS);
export const VET_TABLE = { ...boardTable(PETS.second ?? {}), tiles: PETS.second?.tiles ?? [] };

/** Tile id → the stored `row_kind`. The kind column the two tables are split on. */
export const ROW_KIND: Readonly<Record<string, string>> = { pet: "Pet", vet: "Vet" };
const TILE_OF_ROW_KIND = new Map(Object.entries(ROW_KIND).map(([id, v]) => [v, id]));

/**
 * The tile a row belongs to. A row typed on the planner page before `row_kind` existed carries none,
 * and reads as a PET — which is what it is: every pre-s224 row of this section is an animal, and the
 * vet lived in columns beside it rather than in a row of its own. Pure.
 */
export function tileOfPetRow(r: Pick<SectionRow, "values">): string {
  return TILE_OF_ROW_KIND.get(r.values[FIELD_KEYS.ROW_KIND] ?? "") ?? "pet";
}

/** A row is a real answer once it carries anything the board asks that row's table for. Pure. */
export function hasPetContent(r: Pick<SectionRow, "values">): boolean {
  const v = r.values;
  // The row's Note is an answer on either table (the board draws Note on every row), so a row holding only
  // a note is still drawn when the screen is opened again.
  const keys: string[] = tileOfPetRow(r) === "vet"
    ? [FIELD_KEYS.VET_PRACTICE, FIELD_KEYS.VET_CONTACT, FIELD_KEYS.NOTES]
    : [FIELD_KEYS.PET_NAME, FIELD_KEYS.PET_TYPE, FIELD_KEYS.GUARDIAN, FIELD_KEYS.NOTES];
  return keys.some((k) => (v[k] ?? "").trim() !== "");
}

/** "Who would take them?" — the ONE standard contact card, the row's own guardian answers. */
const WHO_SLOTS: CardSlot[] = [{
  label: "Person 1", name: { kind: "contact", key: FIELD_KEYS.GUARDIAN },
  phone: FIELD_KEYS.GUARDIAN_PHONE, email: FIELD_KEYS.GUARDIAN_EMAIL, address: FIELD_KEYS.GUARDIAN_ADDRESS,
}];

/** "Who does your executor call?" — ONE company card (s236, D-236.25(3); rule 150): the practice IS the company (Company name ·
 *  Main phone · Email or website), then "+ Add a person to ask for" — the school card's shape (DayToDayScreen). */
export const CALL_SLOTS: CardSlot[] = [
  { label: "The company", name: { kind: "contact", key: FIELD_KEYS.VET_CONTACT },
    phone: FIELD_KEYS.VET_CONTACT_PHONE, email: FIELD_KEYS.VET_CONTACT_EMAIL, address: FIELD_KEYS.VET_CONTACT_ADDRESS, org: true },
  { label: "A person to ask for", name: { kind: "contact", key: FIELD_KEYS.VET_ASK_FOR },
    phone: FIELD_KEYS.VET_ASK_FOR_PHONE, email: FIELD_KEYS.VET_ASK_FOR_EMAIL, address: FIELD_KEYS.VET_ASK_FOR_ADDRESS },
];

/** s236 (D-236.40(1)): the practice's name also keeps the role's label (vet_practice → role_label) — every reader of
 *  that label (the secure-pets task, the planner page, the × label) reads the company's name. */
export function withPracticeLabel(changes: Record<string, string>): Record<string, string> {
  if (!(FIELD_KEYS.VET_CONTACT in changes)) return changes;
  const raw = changes[FIELD_KEYS.VET_CONTACT] ?? "";
  return { ...changes, [FIELD_KEYS.VET_PRACTICE]: (parseContact(raw)?.display ?? raw).trim() };
}

export function PetsScreen({ clientId, onAnswered }: { clientId: string; onAnswered: () => void }) {
  return (
    <OnePanelProvider>
      <PetsScreenBody clientId={clientId} onAnswered={onAnswered} />
    </OnePanelProvider>
  );
}

function PetsScreenBody({ clientId, onAnswered }: { clientId: string; onAnswered: () => void }) {
  const data = useSectionRows(clientId, CATEGORY_SLUGS.PERSONAL_INFORMATION, SECTION_SLUGS.FAMILY_PETS, onAnswered, { typed: true });
  const { rows, fieldIdByKey } = data;
  const panels = usePanels();
  // Creating a row is Class A (a second fire is a second animal) — ONE lock for every path that creates one.
  const createFlight = useSingleFlight();
  const [createdHere, setCreatedHere] = useState<ReadonlySet<string>>(() => new Set());

  // ANSWER-FLAG-WRITE-1, kept from the pilot: the existing nine-flag route, a caller not a new writer.
  // s228 (INTERVIEW-SAVE-FAILURE): a refused save puts up the red pop-up (saveFailure.ts).
  const onSaveFailed = useSaveFailure();
  const flagMutation = useMutation({
    mutationFn: async (hasPets: boolean) =>
      await apiRequest("PATCH", `/api/clients/${clientId}/situation-flags`, { hasPets }),
    onError: onSaveFailed,
  });

  const create = (tileId: string) =>
    createFlight.run(() => data.createRow({ [FIELD_KEYS.ROW_KIND]: ROW_KIND[tileId] }))
      .then((id) => { if (id) setCreatedHere((s) => new Set(s).add(id)); });

  const visible = rows.filter((r) => !r.hidden);
  const [selection, setSelection] = useState<TileSelection | null>(null);
  // Seeded ONCE from what the plan holds, then owned here — a refetch mid-answer must not flip a pressed tile.
  useEffect(() => {
    if (selection || !data.ready) return;
    const selected = TILES.map((t) => t.id).filter((id) => visible.some((r) => hasPetContent(r) && tileOfPetRow(r) === id));
    setSelection({ selected, none: false });
  }, [selection, data.ready, visible]);
  const sel = selection ?? { selected: [], none: false };

  const turnOn = async (tileId: string) => {
    const ofTile = rows.filter((r) => tileOfPetRow(r) === tileId);
    if (ofTile.some((r) => r.hidden)) {
      for (const r of ofTile.filter((x) => x.hidden)) await data.setRowHidden(r.rowId, false);
      data.refresh();
    } else if (ofTile.length === 0) {
      await create(tileId);
    } else if (!ofTile.some(hasPetContent)) {
      // Only a blank row of this kind (a tap never answered): draw it as the starter rather than start another.
      setCreatedHere((s) => new Set(s).add(ofTile[ofTile.length - 1].rowId));
    }
    await flagMutation.mutateAsync(true);
  };
  const turnOff = async (tileId: string) => {
    for (const r of rows.filter((x) => tileOfPetRow(x) === tileId && !x.hidden)) await data.setRowHidden(r.rowId, true);
    data.refresh();
  };

  const onTiles = async (next: TileSelection, changed: string) => {
    setSelection(next);
    if (changed === "none") {
      if (!next.none) return;
      // "No animals": the rows it replaces are hidden, and the flag is the answer (it prunes `secure-pets`).
      for (const r of visible) await data.setRowHidden(r.rowId, true);
      data.refresh();
      await flagMutation.mutateAsync(false);
      return;
    }
    if (next.selected.includes(changed)) await turnOn(changed);
    else await turnOff(changed);
  };

  // × HIDES the row and leaves "… removed · Undo" in its place for this visit (G8).
  const [removedHere, setRemovedHere] = useState<ReadonlyMap<string, string>>(() => new Map());
  const removeRow = async (rowId: string) => {
    const r = rows.find((x) => x.rowId === rowId);
    const label = tileOfPetRow(r ?? { values: {} }) === "vet"
      ? (r?.values[FIELD_KEYS.VET_PRACTICE] ?? "").trim() || (parseContact(r?.values[FIELD_KEYS.VET_CONTACT] ?? "")?.display ?? "").trim() || "This practice"
      : (r?.values[FIELD_KEYS.PET_NAME] ?? "").trim() || "This animal";
    setRemovedHere((m) => new Map(m).set(rowId, label));
    try {
      await data.setRowHidden(rowId, true);
    } catch (err) {
      // s228 (ruling 4): a refused × puts the row back — never "… removed · Undo" for a removal that did not happen.
      setRemovedHere((m) => { const n = new Map(m); n.delete(rowId); return n; });
      throw err;
    }
    data.refresh();
  };
  const undoRemove = async (rowId: string) => {
    await data.setRowHidden(rowId, false);
    setRemovedHere((m) => { const n = new Map(m); n.delete(rowId); return n; });
    data.refresh();
  };

  // The rows drawn: a picked tile's rows (answered, or started this visit), and rows removed this visit.
  const drawn = rows.filter((r) => {
    if (removedHere.has(r.rowId)) return true;
    if (r.hidden) return false;
    return sel.selected.includes(tileOfPetRow(r)) && (hasPetContent(r) || createdHere.has(r.rowId));
  });

  const tiled: TiledRow[] = drawn.map((r) => {
    const tileId = tileOfPetRow(r);
    const tile = TILES.find((t) => t.id === tileId);
    const lead = tile?.label ?? "";
    if (removedHere.has(r.rowId)) {
      return { rowId: r.rowId, tileId, lead, cells: [], removed: { label: removedHere.get(r.rowId)!, onUndo: () => saveInBackground(undoRemove(r.rowId)) } };
    }
    const v = r.values;
    const isVet = tileId === "vet";
    const tid = (what: string) => `interview-pets-${what}-${r.rowId}`;
    const cardKey = `${r.rowId}:card`;
    const noteKey = `${r.rowId}:note`;
    // Each cell's accessible name is its column's header on the board.
    const table = isVet ? VET_TABLE : PET_TABLE;
    const headerOf = (k: string) => table.columns.find((c) => c.key === k)?.label ?? "";
    const save = (changes: Record<string, string>) => data.writeCells(r.rowId, isVet ? withPracticeLabel(changes) : changes);
    const cardCell = isVet ? CELL.call : CELL.who;
    const slots = isVet ? CALL_SLOTS : WHO_SLOTS;

    return {
      rowId: r.rowId,
      tileId,
      lead,
      ...(tile?.mark && tile.color ? { leadMark: { mark: tile.mark, color: tile.color } } : {}),
      cells: isVet
        ? [
            // s236 (D-236.25(3)): ONE company cell — the plain practice box is gone; the cell names the company only.
            <LinkedPersonCell key="call" value="" label={cardSummary(CALL_SLOTS.slice(0, 1), v)} word={CELL.call.word ?? ""} org={CELL.call.org}
              open={panels.isOpen(cardKey)} onOpen={() => panels.toggle(cardKey)} testId={tid("call")} />,
          ]
        : [
            <ConsequencePicker key="kind" value={v[FIELD_KEYS.PET_TYPE] ?? ""} options={PET_KINDS} placeholder={CELL.kind.ph}
              ariaLabel={headerOf("kind")} testId={tid("kind")} onCommit={(val) => saveInBackground(save({ [FIELD_KEYS.PET_TYPE]: val }))} />,
            <IconTextCell key="name" value={v[FIELD_KEYS.PET_NAME] ?? ""} placeholder={CELL.name.ph}
              ariaLabel={headerOf("name")} testId={tid("name")} onCommit={(val) => saveInBackground(save({ [FIELD_KEYS.PET_NAME]: val }))} />,
            // 🔴 The question the screen exists for, and the one the built screen never asked.
            <LinkedPersonCell key="who" value="" label={cardSummary(WHO_SLOTS, v)} word={CELL.who.word ?? ""}
              open={panels.isOpen(cardKey)} onOpen={() => panels.toggle(cardKey)} testId={tid("who")} />,
          ],
      // The board declares no `noDup`, and its own row note says one row per animal or per herd.
      canAddRow: true,
      // s226 (CH1-ROW-NOTES-COLUMN-1): the board draws Note on every row of both tables (Main.dc.html, the row
      // actions). `family-pets` already carries `notes`; the pilot never wired it.
      note: {
        value: v[FIELD_KEYS.NOTES] ?? "",
        open: panels.isOpen(noteKey),
        onToggle: () => panels.toggle(noteKey),
        // s237 (D-237.30): the save is returned, so the Note's Done waits for it.
        onCommit: (val) => (val !== (v[FIELD_KEYS.NOTES] ?? "") ? save({ [FIELD_KEYS.NOTES]: val }) : undefined),
      },
      panels: panels.isOpen(cardKey) ? (
        <ContactCard interviewLook key={cardKey} title={cardCell.title ?? ""} slots={slots} values={v} fieldIdByKey={fieldIdByKey} clientId={clientId}
          undo={cardCell.undo} {...(isVet ? { moreLabel: "+ Add a person to ask for" } : {})} onSave={save} onClose={() => panels.close(cardKey)} testId={tid("card")} />
      ) : null,
    };
  });

  return (
    <div data-testid="interview-pets">
      <TilePicker compact tiles={TILES} selection={sel} onChange={(n, c) => saveInBackground(onTiles(n, c))} none={screenByKey("pets")?.none}
        columns={PETS_TILES.cols as 2 | 3 | 4 | 5} testIdPrefix="interview-pets" />
      <TileTables rows={tiled} first={PET_TABLE} second={VET_TABLE}
        onAddRow={(rowId) => { const r = rows.find((x) => x.rowId === rowId); if (r) saveInBackground(create(tileOfPetRow(r))); }}
        onRemove={(id) => saveInBackground(removeRow(id))} testIdPrefix="interview-pets-tables" cutLead rowMarks strongRowLine />
      <div className="mt-6 flex flex-col gap-1.5">
        {TILES.filter((t) => sel.selected.includes(t.id) && NOTE_FOR[t.id]).map((t) => (
          <NoteCard key={t.id} mark={t.mark ?? ""} color={t.color ?? ""} testId={`interview-pets-note-${t.id}`}>{NOTE_FOR[t.id]}</NoteCard>
        ))}
      </div>
    </div>
  );
}
